import { memo, useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useAmp } from 'next/amp'

import InjectedStylesCatcher from '~/components/injectedStylesCatcher/injectedStylesCatcher';
import useSetVideo from '~/hooks/useSetVideo';
import SiteSettings from '~/siteSettings';
import Store from '~/store/undux';
import YouTubeVideoBlock from '~/components/videoBlock/youtubeVideoBlock';
import VideoAmp from '../../pages/videos/videoAmp';
import VideoBlock from '~/components/videoBlock/videoBlock';

import {
  getStructuredData,
  setUrl
} from '~/utils/utils';

const Utag = dynamic(() => import('~/components/utag/utag'), { ssr: false });
const BidBarrelReact = dynamic(() => import('~/components/bidBarrelReact/bidBarrelReact'), { ssr: false });

const BIDBARREL_AD_UNITS = {
  mobile: [
    'mobile-plus-flex-top',
    'mobile-plus-flex-middle',
    'mobile-plus-flex-inc',
    'mobile-plus-flex-bottom'
  ],
  desktop: [
    'leader-plus-flex-top',
    'leader-plus-flex-middle',
    'leader-plus-flex-inc',
    'leader-plus-flex-bottom'
  ]
};

/**
 * Reusable Video Page Component
 */
const VideoPage = ({
  content,
  playlistIndex,
  oldMpxVideo,
  trayIndex,
  youtubePlaylists,
  youTubeVideoId,
  drupalSettings
}) => {
  const store = Store.useStore();
  const isAmp = useAmp();
  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;
  const { updateStore } = useSetVideo();

  const playlistInfo = store.get?.('playlistInfo');
  const storePlaylist = useMemo(
    () => (playlistInfo?.activePlaylist?.length > 0 ? playlistInfo.activePlaylist : youtubePlaylists[trayIndex]?.items),
    [playlistInfo?.activePlaylist, youtubePlaylists, trayIndex]
  );
  const storePlaylistIndex = typeof playlistInfo?.playlistIndex === 'number' ? playlistInfo.playlistIndex : playlistIndex;
  const activeItem = storePlaylist?.[storePlaylistIndex];
  const node = activeItem ? activeItem : content || oldMpxVideo?.nodeQuery;

  const keywords = useMemo(
    () => [SiteSettings.siteName, 'videos', 'video'],
    [content]
  );

  const utagData = useMemo(
    () => ({
      sitePrimaryRsid: 'cbsidrewbarrymoresite',
      assetType: 'video',
      editorialContentName: node?.title,
      editorialContentId: node?.nid,
      videoTitle: node?.title,
      ...(keywords.length > 0 && { keywords })
    }),
    [node?.title, node?.nid, keywords]
  );

  useEffect(() => {
    if (!isAmp) {
      if (!oldMpxVideo) {
        updateStore(trayIndex, playlistIndex, youtubePlaylists, youtubePlaylists[trayIndex]?.category, 'video node page');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delay = typeof window !== 'undefined' && window?.location?.pathname === '/videos' ? 500 : 0;
    const id = setTimeout(() => {
      if (!oldMpxVideo) {
        setUrl(activeItem?.path)
      }
    }, delay);
    return () => clearTimeout(id);
  }, [activeItem?.path, oldMpxVideo]);

  if (node === null) {
    return null;
  }
  
  return (
    <>
      <Head>
        <title>{node.title} | {SiteSettings.siteName}</title>
        <meta name='title' content={node.title} />
        <link rel='canonical' href={`${baseUrl}${node.path}`} />
        <meta name='twitter:title' content={node.field_social_headline || node.title} />
        <meta name='description' content={node.field_seo_description || node.field_subhead} />
        <meta property='og:url' content={`${baseUrl}${node.path}`} />
        <meta property='og:title' content={node.field_social_headline || node.title} />
        <meta property='og:image' content={node?.field_image?.field_image?.social?.url} />
        <meta property='og:description' content={node.field_social_description || node.field_subhead} />
        <link rel='image_src' href={node?.field_image?.field_image?.social?.url} />
        <meta name='twitter:url' content={`${baseUrl}${node.path}`} />
        <meta name='twitter:image:width' content={node?.field_image?.field_image?.social?.width} />
        <meta name='twitter:image:height' content={node?.field_image?.field_image?.social?.height} />
        <meta name='twitter:image' content={node?.field_image?.field_image?.social?.url} />
        <meta property='article:published_time' content={node.field_display_date} />
        <meta name='keywords' content={keywords.join(', ') || SiteSettings.defaultKeywords} />
        <meta name='robots' content={`${(node.field_is_promotional || node?.field_is_unlisted) ? 'noindex' : 'index'}, max-image-preview:large`} />
      </Head>
      {isAmp ? (
        <VideoAmp
          content={ content || oldMpxVideo.nodeQuery }
          drupalSettings={ drupalSettings }
          keywords={ keywords || SiteSettings.defaultKeywords }
          pageType='videos'
          siteSection='videos'
        />
      ) : (
        <>
          <Head>
            <script
              key='json-ld'
              type='application/ld+json'
              dangerouslySetInnerHTML={{ __html: JSON.stringify(getStructuredData(baseUrl, node.path, node)) }}
            />
          </Head>
          <InjectedStylesCatcher />
          <div className='content'>
            {oldMpxVideo ? (
              <VideoBlock
                data={ oldMpxVideo }
                fileUrl={baseUrl}
                drupalSettings={ drupalSettings }
                parentNid={ oldMpxVideo.nid }
                pageType='video'
                contentCompleteRedirectURL={youtubePlaylists[0]?.items?.[0]?.path || '/videos'}
                section='video'
              />
            ) : (
              <YouTubeVideoBlock
                currentVideo={activeItem}
                data={node}
                drupalSettings={drupalSettings}
                fileUrl={baseUrl}
                parentNid={node.nid}
                pageType='video'
                recommendations={youtubePlaylists[trayIndex]?.items}
                section='video'
              />
            )}
          </div>
          <Utag pageType='video' siteSection='videos' data={utagData} />
          <BidBarrelReact data={node} mapToAdUnits={BIDBARREL_AD_UNITS} />
        </>
      )}
    </>
  );
};

export default memo(VideoPage);
